import { makeObservable, observable, runInAction, computed, action } from 'mobx';
import { ApplicationSdk } from '../../../../core/sdk';

/**
 * The details of a current order
 */
class OrderEditDialogStore {
    private sdk: ApplicationSdk;
    public order_id: number | null = null;

    public order_employee: any = null;
    public order_employee_filter: string = '';

    public order_object: any = null;
    public order_object_filter: string = '';

    constructor(sdk: ApplicationSdk) {
        makeObservable(this, {
            order_employee: observable,
            order_employee_filter: observable,
            order_object: observable,
            order_object_filter: observable,
            selectedEmployees: computed,
            currentEmployeeFilter: computed,
            selectedObject: computed,
            currentObjectFilter: computed,
            isValid: computed,
            selectObject: action,
            selectEmployee: action,
            filterObject: action,
            filterEmployees: action,
        });

        this.sdk = sdk;
    }

    public async init(config: any): Promise<void> {
        this.order_id = config?.order_id || null;
        let promises: any = [];

        if (this.sdk.employee) {
            promises.push(this.sdk.employee.fetch());
        }
        if (this.sdk.objects) {
            promises.push(this.sdk.objects.fetch());
        }

        if (promises.length) {
            await Promise.all(promises);
        }

        runInAction(() => {
            if (config.employee_id) {
                this.order_employee =
                    this.sdk.employee?.all.data.find(
                        (employee: any) => employee.id === config.employee_id
                    )?.id || null;
            }
            if (config.object_id) {
                this.order_object =
                    this.sdk.objects?.all.data.find((obj: any) => obj.id === config.object_id) ||
                    null;
            }
        });
    }

    public selectEmployee(employee: any): void {
        this.order_employee = employee || null;
    }

    public filterEmployees(filter: string): void {
        this.order_employee_filter = filter;
    }

    public selectObject(obj: any): void {
        this.order_object = obj;
    }

    public filterObject(filter: string): void {
        this.order_object_filter = filter;
    }

    public async createOrUpdateOrder(): Promise<any> {
        if (this.isValid) {
            if (this.isNewOrder) {
                const orderData = {
                    employee_id: this.order_employee || null,
                    object_id: this.order_object.id,
                    manager_id: this.sdk.user?.id || null,
                    products: [],
                };

                const response = await this.sdk.fetchApi('order', 'add', orderData);

                if (response.status === 201) {
                    return {
                        id: response.json.id,
                        event: {
                            event: 'NEW',
                        },
                    };
                }
            } else {
                const orderData = {
                    order_id: this.order_id,
                    employee_id: this.order_employee || null,
                    object_id: this.order_object.id,
                };

                const response = await this.sdk.fetchApi('order', 'reassign', orderData);

                if (response.status === 200) {
                    return {
                        id: response.json.id,
                        event: {
                            event: 'UPDATE',
                        },
                    };
                }
            }
        }
        return {
            id: null,
            event: null,
        };
    }

    get id(): string {
        return 'editorder';
    }

    get isNewOrder(): boolean {
        return this.order_id === null;
    }

    get buttonLabel(): string {
        return this.isNewOrder ? 'Auftrag erstellen' : 'Auftrag ändern';
    }

    get header(): string {
        return this.isNewOrder ? 'Neue Bestellung' : 'Bestellung bearbeiten';
    }

    get selectedEmployees(): any {
        return this.order_employee;
    }

    get currentEmployeeFilter(): string {
        return this.order_employee_filter;
    }

    get selectedObject(): any {
        return this.order_object;
    }

    get currentObjectFilter(): string {
        return this.order_object_filter;
    }

    get isValid(): boolean {
        return this.order_object !== null;
    }
}

export default OrderEditDialogStore;
